import React, { useCallback } from 'react';
import * as BS from 'react-bootstrap';
import * as BSEx from '../../BootstrapExtends';
import PropTypes from 'prop-types';
import Panel from '../../../models/SpreadsheetReportConfigs/Panel';
import { PeriodOptions } from '../../../enums/SpreadsheetReportConfigs';
import {
  OTHER_COLUMN_VALUES,
  PERIOD_OPTION_VALUES,
} from '../../../constants/SpreadsheetReportConfigs';
import DateRangePicker from './DateRangePicker';
import OutputColumnSelect from './OutputColumnSelect';
import OutputDataFilter from './OutputDataFilter';
import OutputDataSorter from './OutputDataSorter';
import PreviewOutputTableDataModal from '../Modal/PreviewOutputTableDataModal';
import ConversionPoint from '../../../models/SpreadsheetReportConfigs/ConversionPoint';

const scope =
  'frontend.components.SpreadsheetReportConfigs.Form.CustomReportForm.sheetConfigSection.panelSection';

const propTypes = {
  panel: PropTypes.exact(Panel.propTypes),
  isOverPeriodThreshold: PropTypes.bool,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.instanceOf(Object),
  onEventChange: PropTypes.func,
  onValueChange: PropTypes.func,
  onBlur: PropTypes.func,
  conversionPoints: PropTypes.arrayOf(
    PropTypes.shape(ConversionPoint.propTypes)
  ),
  channelOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  shouldHighlightNumberOfMaxLines: PropTypes.bool,
  onInitialFieldFocus: PropTypes.func,
};

const CustomReportTablePanel = ({
  panel,
  isOverPeriodThreshold,
  errors,
  touched,
  onEventChange,
  onValueChange,
  onBlur,
  conversionPoints,
  channelOptions = [],
  shouldHighlightNumberOfMaxLines,
  onInitialFieldFocus,
}) => {
  // カラム変更時に、存在しなくなったカラムを参照するソート条件を除去する
  const handleColumnsChange = useCallback(
    (newColumns) => {
      onValueChange('columns')(newColumns);

      const validContents = new Set(
        newColumns
          .filter((col) => col.content !== OTHER_COLUMN_VALUES.blank)
          .map((col) => col.content)
      );

      // 有効なカラムがなければソートを全削除、あれば無効なソートのみ除去
      const cleanedSorts =
        validContents.size === 0
          ? []
          : panel.sorts.filter(
              (sort) =>
                sort.targetColumn === '' || validContents.has(sort.targetColumn)
            );

      if (cleanedSorts.length !== panel.sorts.length) {
        onValueChange('sorts')(cleanedSorts);
      }
    },
    [onValueChange, panel.sorts]
  );

  return (
    <>
      {/* Number of Max Lines */}
      <BSEx.FormGroupInline
        className={
          shouldHighlightNumberOfMaxLines
            ? 'custom-report-form-field-unfocused'
            : undefined
        }
        label={I18n.t(`${scope}.field.numberOfMaxLines.label`)}
        error={errors.numberOfMaxLines}
        touched={touched.numberOfMaxLines}
      >
        <BS.FormControl
          placeholder={I18n.t(`${scope}.field.numberOfMaxLines.placeholder`)}
          type='number'
          name='numberOfMaxLines'
          value={panel.numberOfMaxLines}
          onChange={onEventChange('numberOfMaxLines')}
          onBlur={() => onBlur('numberOfMaxLines')}
          onFocus={
            onInitialFieldFocus
              ? onInitialFieldFocus('numberOfMaxLines')
              : undefined
          }
        />
      </BSEx.FormGroupInline>

      {/* Period */}
      <BSEx.FormGroupInline
        label={I18n.t(`${scope}.field.period.label`)}
        error={errors.period}
        touched={touched.period}
      >
        <BS.FormControl
          componentClass='select'
          name='period'
          value={panel.period}
          onChange={onEventChange('period')}
          onBlur={() => onBlur('period')}
        >
          {Object.entries(PeriodOptions).map(([key, option]) => (
            <option key={key} value={option.value}>
              {option.label}
            </option>
          ))}
        </BS.FormControl>
      </BSEx.FormGroupInline>

      {/* startedOn and endedOn */}
      {panel.period === PERIOD_OPTION_VALUES.custom_period && (
        <DateRangePicker
          startDateLabel={I18n.t(`${scope}.field.startDate.label`)}
          endDateLabel={I18n.t(`${scope}.field.endDate.label`)}
          startDate={panel.startedOn}
          endDate={panel.endedOn}
          errors={{
            startDate: errors.startedOn,
            endDate: errors.endedOn,
          }}
          touched={{
            startDate: touched.startedOn,
            endDate: touched.endedOn,
          }}
          onChangeStartDate={onValueChange('startedOn')}
          onChangeEndDate={onValueChange('endedOn')}
          onBlurStartDate={() => onBlur('startedOn')}
          onBlurEndDate={() => onBlur('endedOn')}
        />
      )}

      <OutputColumnSelect
        isOverPeriodThreshold={isOverPeriodThreshold}
        columns={panel.columns}
        filters={panel.filters || []}
        onChange={handleColumnsChange}
        conversionPoints={conversionPoints}
      />

      <OutputDataFilter
        filters={panel.filters || [[]]}
        selectedColumns={panel.columns}
        errors={errors}
        touched={touched}
        conversionPoints={conversionPoints}
        onChange={onValueChange('filters')}
        onBlur={onBlur}
        channelOptions={channelOptions}
      />

      <OutputDataSorter
        selectedColumns={panel.columns}
        sorts={panel.sorts}
        errors={errors}
        touched={touched}
        onChange={onValueChange('sorts')}
        onBlur={onBlur}
      />

      <PreviewOutputTableDataModal panel={panel} columns={panel.columns} />
    </>
  );
};

CustomReportTablePanel.propTypes = propTypes;

export default CustomReportTablePanel;
